import React, { useState } from 'react';

import gql from 'graphql-tag';
import { useLazyQuery } from 'react-apollo';
import ReactGA from 'react-ga4';

import * as actions from 'actions';
import TextInput from 'Components-redesign/Fields/TextInput';
import Modal from 'Components-redesign/Modal';
import ModalContentWrapper from 'Components-redesign/Modal/ModalContentWrapper';
import Notice from 'Components-redesign/Notice';
import { Heading } from 'Components-redesign/Typography/Heading';
import { useDispatch, useI18n, useLogger } from 'Hooks';

import { ErrorMessage, LabelText, ModalContent, SubmitButton, Text } from './styles';

const RESET_PASSWORD_QUERY = gql`
  query passwordResetSendEmail($username: String!) {
    passwordResetSendEmail(username: $username) {
      success
      error
      message
    }
  }
`;

interface Props {
  isShowing: boolean;
  hide: () => void;
}

const ForgotPassword: React.FC<Props> = ({ isShowing, hide }) => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const logger = useLogger('forgot-password');

  const [username, setUsername] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const closeModal = () => {
    setUsername('');
    setErrorMessage('');
    hide();
  };

  const [resetPassword, { loading }] = useLazyQuery(RESET_PASSWORD_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.passwordResetSendEmail.success) {
        logger.info(`Password reset email was sent to user: ${username}`);
        ReactGA.event({
          category: 'Reset password',
          action: 'Reset_password_email_sent',
        });
        Promise.resolve(1)
          .then(() =>
            dispatch(actions.showNotice(t('ResetPassword.EmailHasBeenSentToYourEmail'), 3000)),
          )
          .then(() => setTimeout(() => closeModal(), 3000));
      } else {
        setErrorMessage(data.passwordResetSendEmail.message);
      }
    },
  });

  const handleChange = (e) => {
    if (errorMessage.length > 0) {
      setErrorMessage('');
    }
    setUsername(e.target.value);
  };

  return (
    <>
      <Modal isShowing={isShowing} hide={closeModal} showCloseBtn={true}>
        <ModalContentWrapper width="448px">
          <ModalContent>
            <Heading level={2}>{t('ResetPassword.ForgotYourPassword')}</Heading>
            <Text>{t('ResetPassword.EnterUsername')}</Text>
            <LabelText>{t('General.Username')}</LabelText>
            <TextInput
              name="username"
              value={username}
              handleChange={handleChange}
              style={errorMessage.length > 0 ? { borderColor: 'var(--colorUIRed)' } : {}}
            />
            <ErrorMessage>{errorMessage}</ErrorMessage>
            <SubmitButton
              type="submit"
              onClick={() => resetPassword({ variables: { username } })}
              disabled={!username.length || loading}>
              {loading ? (
                <>{t('General.LoadingThreeDots')}</>
              ) : (
                <span>{t('ResetPassword.SendLink')}</span>
              )}
            </SubmitButton>
          </ModalContent>
        </ModalContentWrapper>
      </Modal>

      <Notice color={'#A6D6B5'} />
    </>
  );
};

export default ForgotPassword;
